import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft, Buildings, Calendar, CalendarBlank, CaretRight, ChatCircleText,
    CheckCircle, Certificate, Clock, GraduationCap, LinkSimple, MapPin, Newspaper,
    Phone, PhoneCall, ShareNetwork, ShieldCheck, Sparkle, Star, Translate, User,
    WhatsappLogo,
} from "@phosphor-icons/react";
import { CallbackModal } from "@/pages/DoctorLandingPage";

/**
 * PublicArticlePage — /dr/:doctorSlug/article/:articleSlug
 *
 * Rendered on its own URL for SEO indexability. Layout:
 *   - Full-width breadcrumb bar   (Home / Dr. Name / Article title)
 *   - Two-column below:
 *       Left (2/3):  hero (title, meta), long-form body
 *       Right (1/3): sticky doctor sidebar (photo, chips, booking CTA)
 *   - "More from this doctor" strip at bottom
 *
 * Meta tags set via React 19's native support for <title> / <meta> in JSX.
 * Google indexes the URL; social platforms get proper preview cards.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Accent palette mirrored from DoctorLandingPage.jsx to keep visuals aligned. */
const ACCENTS = {
    teal:  { base: "hsl(192 90% 33%)",  soft: "hsl(192 90% 96%)",  ring: "hsl(192 90% 88%)",  text: "hsl(192 90% 25%)" },
    rose:  { base: "hsl(340 75% 45%)",  soft: "hsl(340 75% 97%)",  ring: "hsl(340 75% 90%)",  text: "hsl(340 75% 35%)" },
    amber: { base: "hsl(28 88% 45%)",   soft: "hsl(28 88% 97%)",   ring: "hsl(28 88% 88%)",   text: "hsl(28 88% 35%)" },
    slate: { base: "hsl(215 60% 32%)",  soft: "hsl(215 60% 97%)",  ring: "hsl(215 60% 88%)",  text: "hsl(215 60% 25%)" },
};
const pickAccent = (n) => ACCENTS[n] || ACCENTS.teal;

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
};

const readingTimeMin = (text) => Math.max(1, Math.round((text || "").split(/\s+/).length / 200));


export default function PublicArticlePage() {
    const { doctorSlug, articleSlug } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, article: null, doctor: null, more: [] });
    const [callbackOpen, setCallbackOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setState((s) => ({ ...s, loading: true }));
            try {
                const { data } = await axios.get(`${API}/public/doctor-page/${doctorSlug}/article/${articleSlug}`);
                if (cancelled) return;
                setState({
                    loading: false,
                    article: data.article,
                    doctor: data.doctor,
                    more: data.more || [],
                });
                // Redirect to canonical slug if the doctor slug in URL is a legacy alias.
                if (data.canonical_slug && data.canonical_slug !== doctorSlug) {
                    navigate(`/dr/${data.canonical_slug}/article/${articleSlug}`, { replace: true });
                }
            } catch (err) {
                if (cancelled) return;
                setState({ loading: false, article: null, doctor: null, more: [], error: err?.response?.status || 500 });
            }
        })();
        return () => { cancelled = true; };
    }, [doctorSlug, articleSlug, navigate]);

    if (state.loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-sm text-muted-foreground">Loading article…</div>
            </div>
        );
    }

    if (!state.article) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                    <Newspaper size={48} className="mx-auto text-slate-300 mb-3" />
                    <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Article not found</h1>
                    <p className="text-sm text-slate-500 mb-4">This article may have been removed or the URL has changed.</p>
                    <Link to={`/dr/${doctorSlug}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                        <ArrowLeft size={14} /> Back to Dr. profile
                    </Link>
                </div>
            </div>
        );
    }

    const a = state.article.data || {};
    const doc = state.doctor;
    const accent = pickAccent(doc?.accent);
    const readMins = readingTimeMin(a.body || a.summary);
    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    const share = async () => {
        const shareData = { title: a.title, text: a.summary, url: shareUrl };
        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(shareUrl);
                alert("URL copied to clipboard");
            }
        } catch { /* user cancelled */ }
    };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="public-article-page">
            {/* Meta tags — React 19 hoists these into <head> */}
            <title>{`${a.title} · ${doc.name} · Invisique™`}</title>
            <meta name="description" content={a.summary?.slice(0, 160) || a.title} />
            <meta property="og:title" content={a.title} />
            <meta property="og:description" content={a.summary?.slice(0, 160) || a.title} />
            <meta property="og:type" content="article" />
            <meta property="og:url" content={shareUrl} />
            {a.cover_image && <meta property="og:image" content={a.cover_image} />}
            <meta name="twitter:card" content="summary_large_image" />
            {/* Structured data — Google shows richer results with this */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                headline: a.title,
                description: a.summary,
                // iter100.169 — Google's Article schema recommends `image`
                // ALWAYS be present. Prefer the article's cover, fall back to
                // the doctor's headshot so cover-less articles still get a
                // rich card in search results.
                image: [a.cover_image, doc.photo].filter(Boolean),
                datePublished: a.published_at || state.article.reviewed_at || state.article.created_at,
                dateModified: state.article.updated_at || state.article.reviewed_at || a.published_at || state.article.created_at,
                author: {
                    "@type": "Person",
                    name: doc.name,
                    jobTitle: doc.specialization,
                    // iter100.169 — author.url ties the byline back to the
                    // doctor's marketing profile → Google can crawl the link
                    // graph and treat the doctor as a first-class entity
                    // (stronger E-E-A-T signal for medical content).
                    url: doc.slug && typeof window !== "undefined"
                        ? `${window.location.origin}/dr/${doc.slug}`
                        : undefined,
                    worksFor: doc.clinic_name ? {
                        "@type": "Organization",
                        name: doc.clinic_name,
                        address: doc.clinic_city || undefined,
                    } : undefined,
                },
                publisher: { "@type": "Organization", name: "Invisique™", logo: { "@type": "ImageObject", url: `${typeof window !== "undefined" ? window.location.origin : ""}/logo.png` } },
                mainEntityOfPage: { "@type": "WebPage", "@id": shareUrl },
            })}} />

            {/* Breadcrumb */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-2 text-[12px] text-slate-600 flex-wrap">
                    <Link to="/" className="hover:text-slate-900" data-testid="breadcrumb-home">Home</Link>
                    <CaretRight size={10} weight="bold" className="opacity-50" />
                    <Link to={`/dr/${doc.slug}`} className="hover:text-slate-900" data-testid="breadcrumb-doctor">
                        {doc.name}
                    </Link>
                    <CaretRight size={10} weight="bold" className="opacity-50" />
                    <span className="text-slate-400">Articles</span>
                    <CaretRight size={10} weight="bold" className="opacity-50" />
                    <span className="text-slate-900 font-medium truncate max-w-[240px] sm:max-w-[440px]" data-testid="breadcrumb-current">{a.title}</span>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-10">
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-8 lg:gap-10">
                    {/* ═══ LEFT COLUMN — Article ═══ */}
                    <article className="min-w-0" data-testid="article-body">
                        {/* iter100.45 — Prominent "Back to profile" pill so
                            readers landing directly from Google always have
                            an obvious way back to the doctor's main page.
                            Complements (doesn't replace) the breadcrumb. */}
                        <Link
                            to={`/dr/${doc.slug}`}
                            className="inline-flex items-center gap-1.5 mb-4 h-8 px-3 rounded-full border text-[12px] font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                            style={{ borderColor: accent.ring }}
                            data-testid="article-back-to-profile"
                        >
                            <ArrowLeft size={12} weight="bold" />
                            Back to {doc.name}&apos;s profile
                        </Link>
                        {/* Kicker + share */}
                        <div className="flex items-start justify-between gap-4 mb-2">
                            <div className="text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" style={{ color: accent.text }}>
                                <Newspaper size={12} weight="fill" />
                                Article by {doc.name}
                            </div>
                            <button
                                type="button"
                                onClick={share}
                                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-600 hover:text-slate-900 transition-colors"
                                data-testid="article-share-btn"
                            >
                                <ShareNetwork size={14} weight="regular" />
                                Share
                            </button>
                        </div>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 leading-tight tracking-tight mb-3" data-testid="article-title">
                            {a.title}
                        </h1>
                        <div className="flex items-center gap-3 text-[12px] text-slate-500 mb-6 flex-wrap">
                            {/* iter100.171 — Fall back to the reviewer's
                                approval timestamp when the legacy
                                free-text `published_at` field is missing
                                (new articles rely on server-side review
                                timestamp instead of a doctor-entered
                                date string). */}
                            {(a.published_at || state.article.reviewed_at) && (
                                <span className="inline-flex items-center gap-1">
                                    <Calendar size={12} weight="regular" /> {fmtDate(a.published_at || state.article.reviewed_at)}
                                </span>
                            )}
                            <span className="inline-flex items-center gap-1">
                                <Clock size={12} weight="regular" /> {readMins} min read
                            </span>
                            {doc.clinic_city && (
                                <span className="inline-flex items-center gap-1">
                                    <MapPin size={12} weight="regular" /> {doc.clinic_city}
                                </span>
                            )}
                        </div>

                        {/* Cover image if any */}
                        {a.cover_image && (
                            <div className="mb-8 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
                                <img src={a.cover_image} alt={a.title} className="w-full h-auto object-cover" />
                            </div>
                        )}

                        {/* Summary as a highlighted lede */}
                        {a.summary && (
                            <p
                                className="text-[15.5px] leading-relaxed text-slate-700 border-l-4 pl-4 mb-8 italic"
                                style={{ borderColor: accent.ring }}
                                data-testid="article-summary"
                            >
                                {a.summary}
                            </p>
                        )}

                        {/* Body */}
                        <ArticleBody text={a.body || a.summary} accent={accent} />

                        {/* Bottom CTA */}
                        <div
                            className="mt-10 rounded-2xl p-6 sm:p-8 border"
                            style={{ backgroundColor: accent.soft, borderColor: accent.ring }}
                            data-testid="article-bottom-cta"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                                <div className="flex-1">
                                    <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: accent.text }}>
                                        Book with {doc.name}
                                    </div>
                                    <div className="mt-1 font-display text-lg font-bold text-slate-900">
                                        Ready to talk to a real orthodontist about your case?
                                    </div>
                                    <p className="text-[13px] text-slate-600 mt-1">
                                        Free 20-minute consultation. 3D scan. No obligation.
                                    </p>
                                </div>
                                <Link
                                    to={`/dr/${doc.slug}#book`}
                                    className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl font-semibold text-white shadow-sm hover:brightness-95 transition-all"
                                    style={{ backgroundColor: accent.base }}
                                    data-testid="article-book-cta"
                                >
                                    Book consultation <CaretRight size={14} weight="bold" />
                                </Link>
                            </div>
                        </div>
                    </article>

                    {/* ═══ RIGHT COLUMN — Sticky Doctor Card ═══ */}
                    <aside className="lg:sticky lg:top-6 h-fit" data-testid="article-doctor-sidebar">
                        <DoctorSidebarCard
                            doctor={doc}
                            accent={accent}
                            onOpenCallback={() => setCallbackOpen(true)}
                        />
                    </aside>
                </div>

                {/* iter100.172 — Author-picked "Related from Dr. X" strip.
                    Rendered ABOVE the auto "More from" strip because it's a
                    curated internal-link set (SEO win + higher CTR than
                    auto-recommended). */}
                {Array.isArray(a.related_items) && a.related_items.length > 0 && (
                    <section
                        className="mt-12 border-t border-slate-200 pt-8"
                        data-testid="article-related-strip"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <span
                                className="h-6 w-6 rounded-full grid place-items-center text-white"
                                style={{ backgroundColor: accent.base }}
                            >
                                <LinkSimple size={12} weight="bold" />
                            </span>
                            <h2 className="font-display text-lg font-bold text-slate-900">
                                Related from {doc.name}
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {a.related_items.map((item) => (
                                <MoreCard
                                    key={item.id}
                                    item={{ id: item.id, kind: item.kind, data: item }}
                                    doctorSlug={doc.slug}
                                    accent={accent}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* More from this doctor */}
                {state.more.length > 0 && (
                    <section className="mt-12 border-t border-slate-200 pt-8" data-testid="article-more-strip">
                        <h2 className="font-display text-lg font-bold text-slate-900 mb-4">
                            More from {doc.name}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {state.more.map((item) => (
                                <MoreCard key={item.id} item={item} doctorSlug={doc.slug} accent={accent} />
                            ))}
                        </div>
                    </section>
                )}
            </div>

            {/* iter100.45 — Request-Callback modal, opened from the sticky
                doctor sidebar. Uses the same component the main landing
                page uses, so lead attribution is identical. */}
            {callbackOpen && (
                <CallbackModal
                    doc={doc}
                    slug={doc.slug}
                    accent={accent}
                    onClose={() => setCallbackOpen(false)}
                    defaultNotes={`I read the article "${a.title}" on ${doc.name}'s page and would like to talk.`}
                />
            )}
        </div>
    );
}


/* -----------------------------------------------------------------------
   ArticleBody — very-lite Markdown-ish renderer
   Splits on double-newlines into blocks; ## → h2, - → li, else <p>.
   Zero deps; renders the current seed content beautifully. Can be swapped
   for react-markdown later if we allow bold / italic / links inline.
   ----------------------------------------------------------------------- */
function ArticleBody({ text, accent }) {
    if (!text) return null;
    // First, promote every "## Heading" line into its own block by ensuring
    // a blank line follows it. Handles single-newline-after-heading writing
    // styles without forcing content authors to double-space.
    const normalised = text.replace(/^(##\s+[^\n]+)\n(?!\n)/gm, "$1\n\n");
    const blocks = normalised.split(/\n\n+/);
    return (
        <div className="space-y-5 text-[15.5px] leading-[1.75] text-slate-800" data-testid="article-body-content">
            {blocks.map((block, i) => {
                const trimmed = block.trim();
                if (!trimmed) return null;
                if (trimmed.startsWith("## ")) {
                    return (
                        <h2
                            key={i}
                            className="font-display text-xl font-bold text-slate-900 mt-8 first:mt-0"
                            style={{ borderLeft: `3px solid ${accent.ring}`, paddingLeft: "0.75rem" }}
                        >
                            {trimmed.slice(3)}
                        </h2>
                    );
                }
                if (trimmed.startsWith("**") && trimmed.endsWith("**") && trimmed.length < 200 && !trimmed.slice(2).includes("**")) {
                    return (
                        <h3 key={i} className="font-semibold text-slate-900 mt-6 text-[16px]">
                            {trimmed.slice(2, -2)}
                        </h3>
                    );
                }
                if (/^[-*]\s/m.test(trimmed) && trimmed.split("\n").every((l) => /^[-*]\s/.test(l.trim()))) {
                    const items = trimmed.split("\n").map((l) => l.trim().replace(/^[-*]\s/, ""));
                    return (
                        <ul key={i} className="list-disc pl-6 space-y-1.5 text-slate-800">
                            {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
                        </ul>
                    );
                }
                return <p key={i}>{renderInline(trimmed)}</p>;
            })}
        </div>
    );
}

/* Inline **bold** rendering — everything else stays plain text. */
function renderInline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) {
            return <strong key={i} className="font-bold text-slate-900">{p.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={i}>{p}</React.Fragment>;
    });
}


/* -----------------------------------------------------------------------
   DoctorSidebarCard — compact sticky doctor identity + booking CTA.
   Shared between PublicArticlePage and PublicCasePage.
   ----------------------------------------------------------------------- */
export function DoctorSidebarCard({ doctor, accent, onOpenCallback }) {
    if (!doctor) return null;
    return (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm" data-testid="sidebar-doctor-card">
            {/* Header photo band */}
            <div
                className="p-5 flex items-center gap-4"
                style={{ backgroundColor: accent.soft, borderBottom: `1px solid ${accent.ring}` }}
            >
                <div className="relative shrink-0">
                    {doctor.photo ? (
                        <img
                            src={doctor.photo}
                            alt={doctor.name}
                            className="h-16 w-16 rounded-full object-cover border-2 border-white shadow-sm"
                            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                        />
                    ) : null}
                    <div
                        className="h-16 w-16 rounded-full bg-slate-200 border-2 border-white shadow-sm flex items-center justify-center"
                        style={{ display: doctor.photo ? "none" : "flex" }}
                    >
                        <User size={26} className="text-slate-400" />
                    </div>
                    {doctor.dci_verified && (
                        <span
                            className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-white flex items-center justify-center shadow"
                            title="DCI Verified"
                        >
                            <ShieldCheck size={14} weight="fill" style={{ color: accent.base }} />
                        </span>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-display text-base font-bold text-slate-900 leading-tight truncate">
                        {doctor.name}
                    </div>
                    <div className="text-[12px] text-slate-600 truncate">{doctor.specialization}</div>
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border"
                         style={{ backgroundColor: "white", borderColor: accent.ring, color: accent.text }}>
                        <Certificate size={9} weight="fill" /> Invisique™ Certified
                    </div>
                </div>
            </div>

            {/* Details */}
            <div className="p-5 space-y-3.5 text-[13px]">
                {doctor.qualifications && (
                    <div className="flex items-start gap-2.5 text-slate-700">
                        <GraduationCap size={14} weight="regular" className="shrink-0 mt-0.5 text-slate-400" />
                        <div className="min-w-0">
                            <span className="text-slate-900 font-medium">{doctor.qualifications}</span>
                            {doctor.years_experience > 0 && (
                                <span className="text-slate-500"> · {doctor.years_experience} years</span>
                            )}
                        </div>
                    </div>
                )}
                {doctor.languages?.length > 0 && (
                    <div className="flex items-start gap-2.5 text-slate-700">
                        <Translate size={14} weight="regular" className="shrink-0 mt-0.5 text-slate-400" />
                        <span className="text-slate-800">{doctor.languages.slice(0, 4).join(" · ")}</span>
                    </div>
                )}
                {doctor.clinic_name && (
                    <div className="flex items-start gap-2.5 text-slate-700">
                        <Buildings size={14} weight="regular" className="shrink-0 mt-0.5 text-slate-400" />
                        <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{doctor.clinic_name}</div>
                            {doctor.clinic_city && (
                                <div className="text-[12px] text-slate-500">{doctor.clinic_city}</div>
                            )}
                        </div>
                    </div>
                )}
                {doctor.consultation_fee > 0 && (
                    <div className="flex items-center gap-2.5 text-slate-700">
                        <Sparkle size={14} weight="regular" className="shrink-0 text-slate-400" />
                        <span>Consult fee <span className="font-semibold text-slate-900">₹{doctor.consultation_fee.toLocaleString("en-IN")}</span></span>
                    </div>
                )}
            </div>

            {/* CTAs */}
            <div className="px-5 pb-5 space-y-2">
                <Link
                    to={`/dr/${doctor.slug}#book`}
                    className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl font-semibold text-white shadow-sm hover:brightness-95 transition-all"
                    style={{ backgroundColor: accent.base }}
                    data-testid="sidebar-book-cta"
                >
                    <CalendarBlank size={15} weight="bold" />
                    Book appointment
                </Link>
                {/* iter100.45 — Row 2: Call + Request Callback (primary lead-capture pair).
                    WhatsApp gets its own row below only if the doctor has one configured. */}
                <div className="grid grid-cols-2 gap-2">
                    {doctor.cta_phone ? (
                        <a
                            href={`tel:${doctor.cta_phone}`}
                            className="h-10 inline-flex items-center justify-center gap-1.5 rounded-xl border font-semibold text-[13px] text-slate-800 hover:bg-slate-50 transition-colors"
                            style={{ borderColor: accent.ring }}
                            data-testid="sidebar-call-cta"
                        >
                            <Phone size={13} weight="fill" /> Call
                        </a>
                    ) : <span />}
                    <button
                        type="button"
                        onClick={onOpenCallback}
                        className="h-10 inline-flex items-center justify-center gap-1.5 rounded-xl border font-semibold text-[13px] text-slate-800 hover:bg-slate-50 transition-colors"
                        style={{ borderColor: accent.ring }}
                        data-testid="sidebar-callback-cta"
                    >
                        <PhoneCall size={13} weight="fill" /> Request callback
                    </button>
                </div>
                {doctor.cta_whatsapp && (
                    <a
                        href={`https://wa.me/${doctor.cta_whatsapp.replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-xl border font-semibold text-[13px] text-slate-800 hover:bg-slate-50 transition-colors"
                        style={{ borderColor: accent.ring }}
                        data-testid="sidebar-wa-cta"
                    >
                        <WhatsappLogo size={13} weight="fill" /> WhatsApp
                    </a>
                )}
                <Link
                    to={`/dr/${doctor.slug}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold text-slate-500 hover:text-slate-800 pt-2"
                    data-testid="sidebar-view-profile"
                >
                    View full profile <CaretRight size={11} weight="bold" />
                </Link>
            </div>
        </div>
    );
}


/* -----------------------------------------------------------------------
   MoreCard — a compact article/case-study preview card used in the
   "More from this doctor" strip. Shared between article + case pages.
   ----------------------------------------------------------------------- */
export function MoreCard({ item, doctorSlug, accent }) {
    if (!item) return null;
    const kind = item.kind;
    const d = item.data || {};
    const isArticle = kind === "article";
    const to = isArticle
        ? `/dr/${doctorSlug}/article/${d.slug}`
        : `/dr/${doctorSlug}/case/${d.slug}`;
    return (
        <Link
            to={to}
            className="rounded-xl bg-white border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all group flex flex-col"
            data-testid={`more-card-${d.slug}`}
        >
            <div className="text-[10.5px] font-bold uppercase tracking-widest mb-1.5 flex items-center gap-1" style={{ color: accent.text }}>
                {isArticle ? <Newspaper size={10} weight="fill" /> : <CheckCircle size={10} weight="fill" />}
                {isArticle ? "Article" : "Case study"}
            </div>
            <h3 className="font-display text-[14.5px] font-bold text-slate-900 leading-snug line-clamp-3 mb-2">
                {d.title}
            </h3>
            <p className="text-[12.5px] text-slate-500 line-clamp-3 mt-auto">
                {d.summary || d.description || d.treatment}
            </p>
            <div className="mt-3 text-[11.5px] font-semibold inline-flex items-center gap-0.5 group-hover:gap-1.5 transition-all" style={{ color: accent.text }}>
                Read {isArticle ? "article" : "case study"} <CaretRight size={11} weight="bold" />
            </div>
        </Link>
    );
}
